// Flow interpreter — D9-5. A pure state machine: (run state, input) → (new state,
// effects). It runs on the device during work and is used by the server to verify
// what a device claims a flow produced. No I/O here: scans arrive already parsed,
// proposals and inquiries are answered by the host through inputs.
import { PROCESS_EVENTS, stepById } from "./definition.js";
import { evaluate, evaluateBoolean } from "./expr.js";
export function startRun(flow, context = {}) {
    const state = {
        flowId: flow.id,
        flowVersion: flow.version,
        process: flow.process,
        stepId: flow.start,
        status: "running",
        data: { ...context },
        many: {},
        emitted: [],
        error: null,
    };
    return enter(flow, state);
}
function ctxOf(state, extra = {}) {
    return { ...state.data, step: { ...extra }, emitted: state.emitted.length };
}
/** Entering a step may complete it immediately (show, emit_event, print, branch…). */
function enter(flow, state) {
    const effects = [];
    let s = state;
    for (;;) {
        if (s.stepId === null) {
            s = { ...s, status: "completed" };
            effects.push({ kind: "completed" });
            return { state: s, effects };
        }
        const step = stepById(flow, s.stepId);
        switch (step.type) {
            case "show":
                effects.push({ kind: "prompt", stepId: step.id, text: step.text });
                if (step.advance === "confirm")
                    return { state: { ...s, status: "awaiting_confirm" }, effects };
                s = advance(flow, s, step);
                continue;
            case "branch":
                s = advance(flow, s, step);
                continue;
            case "emit_event": {
                const payload = {};
                for (const [k, expr] of Object.entries(step.payload))
                    payload[k] = evaluate(expr, ctxOf(s));
                effects.push({ kind: "emit", event: step.event, payload });
                s = { ...s, emitted: [...s.emitted, { event: step.event, payload }] };
                s = advance(flow, s, step);
                continue;
            }
            case "print_label": {
                effects.push({ kind: "print", template: step.template, subject: evaluate(step.subject, ctxOf(s)), printerRole: step.printerRole });
                if (step.advance === "confirm")
                    return { state: { ...s, status: "awaiting_confirm" }, effects };
                s = advance(flow, s, step);
                continue;
            }
            case "propose":
                effects.push({ kind: "propose", strategy: step.strategy, stepId: step.id });
                return { state: s, effects };
            case "inquiry": {
                const params = {};
                for (const [k, expr] of Object.entries(step.params))
                    params[k] = evaluate(expr, ctxOf(s));
                effects.push({ kind: "inquiry", query: step.query, params, stepId: step.id });
                return { state: s, effects };
            }
            case "detour": {
                const input = {};
                for (const [k, expr] of Object.entries(step.input))
                    input[k] = evaluate(expr, ctxOf(s));
                effects.push({ kind: "detour", flow: step.flow, input, stepId: step.id });
                return { state: s, effects };
            }
            default:
                effects.push({ kind: "prompt", stepId: step.id, text: step.title || step.type });
                return { state: s, effects };
        }
    }
}
function resolveNext(next, ctx) {
    if (typeof next === "string" || next === null)
        return next;
    for (const b of next.branch)
        if (evaluateBoolean(b.when, ctx))
            return b.to;
    return next.else;
}
function advance(flow, state, step, extra = {}) {
    const nextId = resolveNext(step.next, ctxOf(state, extra));
    return { ...state, stepId: nextId, status: "running" };
}
/** A step that expects a trade item takes bulk goods too (kind "loose", owner 2026-11-03: a quantity of a
 *  product with no identity) — both are goods, as against a pallet. */
function kindMatches(expect, kind) {
    return expect === "any" || kind === expect || (expect === "trade-item" && kind === "loose");
}
function reject(state, message) {
    return { state, effects: [{ kind: "feedback", result: "rejected", message }] };
}
/** Feed one input to the current step. Pure; returns the new state and effects. */
export function applyInput(flow, state, input) {
    if (state.status === "completed" || state.status === "failed" || state.stepId === null) {
        return reject(state, "flow is not running");
    }
    const step = stepById(flow, state.stepId);
    if (state.status === "awaiting_confirm") {
        if (input.kind !== "confirm")
            return reject(state, "confirmation expected");
        const next = advance(flow, state, step);
        return enter(flow, next);
    }
    const accepted = (s, message = "ok") => {
        const r = enter(flow, s);
        return { state: r.state, effects: [{ kind: "feedback", result: "accepted", message }, ...r.effects] };
    };
    switch (step.type) {
        case "scan_location": {
            if (input.kind !== "scan")
                return reject(state, "scan a location");
            if (input.resolved.as !== "location")
                return reject(state, `expected a location, got ${input.resolved.as}`);
            if (step.roles.length > 0 && !step.roles.some((r) => input.resolved.as === "location" && input.resolved.roles.includes(r))) {
                return reject(state, `location lacks role ${step.roles.join("/")}`);
            }
            const s = { ...state, data: { ...state.data, [step.into]: input.resolved.locationId } };
            return accepted(step.advance === "confirm" ? { ...s, status: "awaiting_confirm" } : advance(flow, s, step));
        }
        case "scan_container": {
            if (input.kind !== "scan")
                return reject(state, "scan a container");
            if (input.resolved.as !== "container")
                return reject(state, `expected a container, got ${input.resolved.as}`);
            if (!kindMatches(step.expect, input.resolved.containerKind))
                return reject(state, `expected a ${step.expect}`);
            const s = { ...state, data: { ...state.data, [step.into]: { containerId: input.resolved.containerId, kind: input.resolved.containerKind, identityKey: input.resolved.identityKey, ai: input.parsed.ai } } };
            return accepted(step.advance === "confirm" ? { ...s, status: "awaiting_confirm" } : advance(flow, s, step));
        }
        case "scan_many": {
            const acc = state.many[step.id] ?? { items: [], keys: [] };
            const isTerminator = input.kind === "terminate" ||
                (input.kind === "scan" &&
                    ((step.terminateOn === "location" && input.resolved.as === "location") ||
                        (step.terminateOn === "logistic-unit" && input.resolved.as === "container" && input.resolved.containerKind === "logistic-unit") ||
                        (step.terminateOn === "done-code" && input.resolved.as === "done-code")));
            if (isTerminator) {
                const expected = step.expectedCount ? evaluate(step.expectedCount, ctxOf(state, { tally: acc.items.length })) : null;
                const data = { ...state.data, [step.into]: acc.items };
                if (step.terminatorInto && input.kind === "scan") {
                    data[step.terminatorInto] = input.resolved.as === "location" ? input.resolved.locationId : input.resolved.as === "container" ? input.resolved.containerId : null;
                }
                const s = { ...state, data, many: { ...state.many, [step.id]: acc } };
                if (typeof expected === "number" && expected !== acc.items.length) {
                    // Sanity check failed (R7.13b c): stay on the step, ask for attention.
                    return {
                        state: { ...s, status: "awaiting_confirm" },
                        effects: [{ kind: "feedback", result: "attention", message: `${acc.items.length} of ${expected} scanned` }],
                    };
                }
                return accepted(advance(flow, s, step, { tally: acc.items.length }), `${acc.items.length} scanned`);
            }
            if (input.kind === "confirm" && state.status === "running") {
                // Operator confirmed a mismatching tally with a reason recorded by the host.
                return accepted(advance(flow, { ...state, data: { ...state.data, [step.into]: acc.items } }, step, { tally: acc.items.length }));
            }
            if (input.kind !== "scan")
                return reject(state, "scan a container or terminate");
            if (input.resolved.as !== "container" && input.resolved.as !== "class")
                return reject(state, `expected a container, got ${input.resolved.as}`);
            if (input.resolved.as === "container" && !kindMatches(step.expect, input.resolved.containerKind))
                return reject(state, `expected a ${step.expect}`);
            const key = input.resolved.as === "container" ? (input.resolved.identityKey ?? input.resolved.containerId) : null;
            if (key && acc.keys.includes(key)) {
                return { state, effects: [{ kind: "feedback", result: "rejected", message: "already scanned in this step" }] };
            }
            const item = input.resolved.as === "container" ? { containerId: input.resolved.containerId, kind: input.resolved.containerKind, identityKey: input.resolved.identityKey, ai: input.parsed.ai } : { classKey: input.resolved.classKey, ai: input.parsed.ai };
            const next = { ...state, many: { ...state.many, [step.id]: { items: [...acc.items, item], keys: key ? [...acc.keys, key] : acc.keys } } };
            return { state: next, effects: [{ kind: "feedback", result: "accepted", message: `${acc.items.length + 1}` }] };
        }
        case "scan_product": {
            if (input.kind !== "scan" || input.resolved.as !== "product")
                return reject(state, "scan a product");
            return accepted(advance(flow, { ...state, data: { ...state.data, [step.into]: input.resolved.productId } }, step));
        }
        case "capture_attribute":
        case "confirm_quantity":
        case "choose":
        case "record_reason": {
            const value = input.kind === "value" || input.kind === "answer" ? input.value : input.kind === "reason" ? input.reason : undefined;
            if (value === undefined)
                return reject(state, `value expected for ${step.type}`);
            return accepted(advance(flow, { ...state, data: { ...state.data, [step.into]: value } }, step));
        }
        case "confirm": {
            if (input.kind !== "confirm")
                return reject(state, "confirmation expected");
            return accepted(advance(flow, state, step));
        }
        case "propose":
        case "inquiry":
        case "detour": {
            if (input.kind !== "answer")
                return reject(state, "answer expected");
            const into = step.type === "detour" ? step.into : step.into;
            const data = into ? { ...state.data, [into]: input.value } : state.data;
            return accepted(advance(flow, { ...state, data }, step));
        }
        case "show":
        case "print_label": {
            if (input.kind !== "confirm")
                return reject(state, "confirmation expected");
            return accepted(advance(flow, state, step));
        }
        case "emit_event":
        case "branch":
            return reject(state, "step completes on entry");
    }
}
/**
 * Events a step type produces implicitly (without an emit_event step), per process:
 * scanning containers in a receiving flow *is* receiving them; printing a label
 * identifies; a put-away or move scan produces the Moved event via emit_event only.
 */
const IMPLICIT_EVENTS = {
    // A scan in a receiving step may also find a deviation (unknown product, missing lot, a
    // tally that differs): that is an Inspected observation with a disposition (§4.5, C4).
    // In a move, the unit may be part of loose stock (no identity of its own): the part is cut
    // off (split) at its place before it moves (owner 2026-10-30: a carton always moves whole).
    scan_container: { receiving: ["received", "inspected"], returns: ["received", "inspected"], picking: ["packed"], move: ["split", "emptied"] },
    // The destination of a move may be a pallet (the kolli goes onto it) or a new pallet issued
    // there (its own received event, then placed) — owner 2026-11-02, racking has beams.
    // Bulk stock put where a row of the same product stands joins it: merged instead of moved (owner 2026-11-03);
    // in a put-away part of a loose row may be cut off (split) before it goes.
    scan_location: { move: ["aggregated", "received", "merged"], put_away: ["aggregated", "disaggregated", "merged", "split"] },
    // Scanning a carton in a picking step takes it for the order (picked); a carton lifted off
    // a stock pallet leaves it (disaggregated); a piece cut for a small customer is a split.
    scan_many: { receiving: ["received", "inspected", "moved"], returns: ["received", "inspected"], counting: ["counted", "moved", "inspected"], picking: ["picked", "split", "disaggregated", "counted", "moved"] },
    print_label: { receiving: ["identified"], split: ["identified"], produce: ["identified"], aggregation: ["identified"], picking: ["identified"], packing: ["identified"] },
    capture_attribute: { receiving: ["observed"] },
};
/** Server-side check (R6.1, D9-5): may this flow version have produced these event types? */
export function eventsAllowedByFlow(flow, eventTypes) {
    const allowed = new Set(PROCESS_EVENTS[flow.process]);
    const produced = new Set();
    for (const s of flow.steps) {
        if (s.type === "emit_event")
            produced.add(s.event);
        const implicit = IMPLICIT_EVENTS[s.type]?.[flow.process];
        if (implicit)
            for (const e of implicit)
                produced.add(e);
    }
    const disallowed = eventTypes.filter((t) => !allowed.has(t) || !produced.has(t));
    return { ok: disallowed.length === 0, disallowed };
}
//# sourceMappingURL=interpreter.js.map